import { Button, Heading } from '@grubhubprod/gh-cookbook-react';
import React, { useRef, useState } from 'react';
import {
  Column,
  Flex,
  Row,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { Address } from '@grubhubprod/order-taking-client-sdk/src/sdk/types/geocode';
import { useParams, useHistory } from 'react-router-dom';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import {
  EnterpriseLocationForm,
  ILocationFormState,
} from 'src/components/enterprise/features/Locations/ManageLocation/LocationForm';
import { ILocation } from 'src/types/Location';
import { SHORT_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { AddressConfirmationModal } from 'src/components/AddressConfirmationModal/AddressConfirmationModal';
import { sdk } from 'src/utilities/sdk';
import { pushError, pushSuccess } from 'src/utilities/notifications';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { EnterpriseManageInvoiceRecipients } from 'src/components/enterprise/EnterpriseInvoiceRecipients/EnterpriseManageInvoiceRecipients';
import { SubtotalInvoiceMethod } from 'src/utilities/invoiceContact.utils';
import { InvoiceMethod } from 'src/types/Corp';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { getHomeBreadcrumb } from 'src/constants/urls';

export interface IAddressConfirmationModalData {
  showModal: boolean;
  suggestedGeoAddresses: Address[];
  location: ILocation;
  currentAddressString: string;
}

const defaultAddressConfirmationModalData = {
  showModal: false,
  suggestedGeoAddresses: [],
  location: null,
  currentAddressString: '',
};

export interface EnterpriseManageLocationProps {
  isEditing?: boolean;
}

export function EnterpriseManageLocation({
  isEditing,
}: EnterpriseManageLocationProps): JSX.Element {
  const { corpid, locationId } =
    useParams<{ corpid: string; locationId: string }>();
  const { push } = useHistory();

  const { corpData } = useAuth();
  const isCorpMultiLocationInvoiceMethod =
    corpData?.invoicingMethod === InvoiceMethod.MULTI_LOCATION;
  const form = useRef(null);
  const [formState, setFormState] = useState<ILocationFormState>({
    isValid: false,
    isSubmitting: false,
  });
  const [addressConfirmedData, setAddressConfirmedData] = useState(null);
  const [addressConfirmationModalData, setAddressConfirmationModalData] =
    useState<IAddressConfirmationModalData>(
      defaultAddressConfirmationModalData
    );
  const [locationDeletionInProgress, setLocationDeletionInProgress] =
    useState(false);
  const shouldDisableCtaButtons =
    formState.isSubmitting || locationDeletionInProgress;

  function formStateCb({ isValid, isSubmitting }): void {
    setFormState({ isValid, isSubmitting });
  }

  function onConfirmAddressCb(
    location: ILocation,
    suggestedGeoAddresses: Address[]
  ): void {
    setAddressConfirmationModalData({
      showModal: true,
      location,
      suggestedGeoAddresses,
      currentAddressString: `${location.street_address1}, ${location.address_locality}, ${location.address_region}, ${location.postal_code}`,
    });
  }

  function onAddressConfirmationSuccess(address: Address) {
    const location: ILocation = addressConfirmationModalData.location;
    setAddressConfirmedData({
      ...location,
      street_address1: address.street_address1,
      street_address2: address.street_address2,
      address_locality: address.address_locality,
      address_region: address.address_region,
      postal_code: address.postal_code,
      latitude: address.latitude,
      longitude: address.longitude,
    });
    setAddressConfirmationModalData(defaultAddressConfirmationModalData);
    form.current.submit(true);
  }

  function onAddressConfirmationClose() {
    setAddressConfirmationModalData(defaultAddressConfirmationModalData);
  }

  async function deleteLocation(): Promise<void> {
    setLocationDeletionInProgress(true);
    try {
      await sdk.http.delete(`/corp-admin/${corpid}/locations/${locationId}`);
      setTimeout(() => {
        pushSuccess(`Your location was removed`);
        push(`/corporate/enterprise/${corpid}/locations`);
      }, SHORT_SEARCH_DELAY);
    } catch (error) {
      pushError(`Your location was not removed. Try again.`);
      setLocationDeletionInProgress(false);
    }
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Stack y="xxx-sm">
          <Heading variant="h1">
            {isEditing ? 'Edit Location' : 'Add New Location'}
          </Heading>
        </Stack>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpid),
            },
            {
              label: 'Locations',
              href: `/corporate/enterprise/${corpid}/locations`,
              useRouterLink: true,
            },
          ]}
        />
      </PageHeader>
      <Stack y="md">
        <PageSection>
          <Row>
            <Column phone={7}>
              <EnterpriseLocationForm
                ref={form}
                locationId={locationId as string}
                corpId={corpid as string}
                onSuccess={() =>
                  push(`/corporate/enterprise/${corpid}/locations`)
                }
                formStateCb={formStateCb}
                onConfirmAddressCb={onConfirmAddressCb}
                existingFormData={addressConfirmedData}
              />
            </Column>
          </Row>
        </PageSection>
      </Stack>
      {isEditing && isCorpMultiLocationInvoiceMethod && (
        <Stack y="md">
          <PageSection>
            <EnterpriseManageInvoiceRecipients
              subtotalMethod={SubtotalInvoiceMethod.LOCATION}
              corpId={corpid as string}
              resourceId={locationId as string}
            />
          </PageSection>
        </Stack>
      )}
      <Row>
        <Column phone={3}>
          <Stack x="md">
            <Button
              isLoading={formState.isSubmitting}
              variant="primary"
              block
              buttonSize="medium"
              disabled={!formState.isValid || shouldDisableCtaButtons}
              onClick={() => form.current.submit()}
            >
              {isEditing ? 'Save location' : 'Add New Location'}
            </Button>
          </Stack>
        </Column>
        <Column phone={3}>
          <Stack x="md">
            <RouterButtonLink
              href={`/corporate/enterprise/${corpid}/locations`}
              buttonSize="medium"
              buttonVariant="secondary"
              useBlockButton
              isButtonDisabled={shouldDisableCtaButtons}
            >
              <Flex justifyContent="center">Cancel</Flex>
            </RouterButtonLink>
          </Stack>
        </Column>
        {isEditing && (
          <Column phone={2}>
            <Button
              isLoading={locationDeletionInProgress}
              variant="tertiary"
              block
              buttonSize="medium"
              disabled={shouldDisableCtaButtons}
              onClick={deleteLocation}
            >
              Delete
            </Button>
          </Column>
        )}
      </Row>
      {addressConfirmationModalData.showModal && (
        <AddressConfirmationModal
          currentAddressString={
            addressConfirmationModalData.currentAddressString
          }
          suggestedGeoAddresses={
            addressConfirmationModalData.suggestedGeoAddresses
          }
          onSuccess={onAddressConfirmationSuccess}
          onClose={onAddressConfirmationClose}
        />
      )}
    </Square>
  );
}
